import React from 'react';
import { X } from 'lucide-react';
import { TableColumn, SubfieldDefinition } from '../../../types/domain';
import { SUBFIELD_DATA_TYPES, getFieldIcon } from './types';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Select } from '../../ui/Select';

interface SchemaSubfieldRowProps {
  col: TableColumn;
  sub: SubfieldDefinition;
  canEdit: boolean;
  onUpdateSubfield: (colId: string, subfieldId: string, field: keyof SubfieldDefinition, value: any) => void;
  onDeleteSubfield: (colId: string, subfieldId: string) => void;
}

export const SchemaSubfieldRow: React.FC<SchemaSubfieldRowProps> = ({
  col,
  sub,
  canEdit,
  onUpdateSubfield,
  onDeleteSubfield,
}) => {
  const SubIcon = getFieldIcon(sub.data_type, sub.key, sub.name);

  return (
    <tr className="bg-cf-bg/30 hover:bg-cf-bg/60 transition-colors border-t border-dashed border-cf-border/40 font-sans text-xs">
      {/* Dedicated Column 1: Tree connector indicator */}
      <td className="py-1.5 px-2 align-middle text-center w-12 min-w-[48px] max-w-[48px]">
        <span className="font-mono text-cf-textMuted/50 text-xs">└</span>
      </td>

      {/* Dedicated Column 2: Subfield Label */}
      <td className="py-1.5 px-3 align-middle w-[240px] min-w-[220px]">
        <div className="flex items-center gap-2 pl-3">
          <div className="p-1 rounded bg-cf-surface border border-cf-border text-cf-textSecondary shrink-0">
            <SubIcon size={11} />
          </div>
          <div className="flex-1 min-w-[110px]">
            <Input
              size="sm"
              type="text"
              disabled={!canEdit}
              value={sub.name}
              onChange={(e) => onUpdateSubfield(col.id, sub.id, 'name', e.target.value)}
              placeholder="Subfield Label"
            />
          </div>
        </div>
      </td>

      {/* Dedicated Column 3: Subfield Key */}
      <td className="py-1.5 px-3 align-middle w-[180px] min-w-[160px]">
        <Input
          size="sm"
          type="text"
          disabled={!canEdit}
          value={sub.key}
          onChange={(e) =>
            onUpdateSubfield(
              col.id,
              sub.id,
              'key',
              e.target.value.toLowerCase().replace(/[^a-z0-9_]+/g, '_')
            )
          }
          placeholder="subfield_key"
          className="text-cf-orange font-mono"
        />
      </td>

      {/* Column 4: Subfield Data Type */}
      <td className="py-1.5 px-3 align-middle w-[180px] min-w-[160px]">
        <Select
          size="sm"
          disabled={!canEdit}
          value={sub.data_type}
          onChange={(e) => onUpdateSubfield(col.id, sub.id, 'data_type', e.target.value)}
          options={SUBFIELD_DATA_TYPES.map((t) => ({ value: t.value, label: t.label }))}
        />
      </td>

      {/* Column 5: Subfield Requirement */}
      <td className="py-1.5 px-3 align-middle text-center w-[120px] min-w-[110px]">
        <Button
          variant={sub.is_required ? 'primary' : 'secondary'}
          size="xs"
          disabled={!canEdit}
          onClick={() =>
            onUpdateSubfield(col.id, sub.id, 'is_required', !sub.is_required)
          }
          className={`min-w-[75px] shrink-0 whitespace-nowrap select-none ${
            sub.is_required
              ? 'bg-indigo-500/15 border-indigo-500/40 text-indigo-400 hover:bg-indigo-500/25'
              : 'bg-cf-bg border-cf-border text-cf-textMuted hover:text-cf-textSecondary hover:bg-cf-surfaceHover'
          }`}
          title="Click to toggle subfield requirement"
        >
          {sub.is_required ? 'Required' : 'Optional'}
        </Button>
      </td>

      {/* Column 6: Subfield Sample / Example */}
      <td className="py-1.5 px-3 align-middle w-[270px] min-w-[260px]">
        <div className="w-full min-w-[180px] max-w-xs">
          <Input
            size="sm"
            type="text"
            disabled={!canEdit}
            value={sub.sample_value || ''}
            onChange={(e) =>
              onUpdateSubfield(col.id, sub.id, 'sample_value', e.target.value)
            }
            placeholder="e.g. sample value"
          />
        </div>
      </td>

      {/* Column 7: Subfield Delete Action */}
      <td className="py-1.5 px-3 align-middle text-right w-[110px] min-w-[100px]">
        {canEdit && (
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={() => onDeleteSubfield(col.id, sub.id)}
            className="text-cf-textMuted hover:text-rose-400"
            title="Remove subfield"
          >
            <X size={13} />
          </Button>
        )}
      </td>
    </tr>
  );
};
